import Icon from '../ui/Icon';
import { formatEuro } from '../../utils/format';
import { getFuelMeta } from './fuelTypes';
import { computeSavings } from './priceForm';

const FILLS_PER_YEAR = 24; // 2 fill-ups per month

/**
 * Mirror of the consumer onboarding "savings" step. Reflects the values being
 * edited (even before they're saved); falls back to the saved values while a
 * draft is incomplete.
 */
export default function SavingsPreview({ prices, drafts, activeId, onSelect }) {
  const price = prices.find((p) => p.id === activeId) ?? prices[0];
  if (!price) return null;

  const meta = getFuelMeta(price.fuelType);
  const { unit } = price;
  const values = computeSavings(drafts[price.id]) ?? computeSavings(price);
  const perFillUp = Math.max(0, values.perFillUp);

  return (
    <div className="bg-surface-container-lowest rounded-2xl p-6 border border-outline-variant/30 shadow-md overflow-hidden">
      <div className="flex items-center justify-between pb-4">
        <div className="flex items-center gap-2" aria-hidden="true">
          <span className="w-2.5 h-2.5 rounded-full bg-surface-container-highest" />
          <span className="w-2.5 h-2.5 rounded-full bg-surface-container-highest" />
          <span className="w-2.5 h-2.5 rounded-full bg-surface-container-highest" />
        </div>
        <span className="text-[11px] font-semibold text-on-surface-variant">iOS / Android Step 3/5</span>
      </div>

      <div className="text-center py-2">
        <div className="inline-block px-3 py-1 rounded-full bg-secondary-fixed/50 text-[#002109] text-[11px] font-bold mb-2">
          YOUR PERSONAL POMPI SAVINGS
        </div>
        <h2 className="text-xl font-bold text-on-surface tracking-tight">
          See what you save every month in the Netherlands
        </h2>
        <p className="text-xs text-on-surface-variant mt-1">
          Select your vehicle fuel type to preview your guaranteed Pompi advantage:
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Preview fuel type"
        className="grid gap-1 p-1 bg-surface-container rounded-lg my-4 text-center"
        style={{ gridTemplateColumns: `repeat(${prices.length}, minmax(0, 1fr))` }}
      >
        {prices.map((p) => {
          const active = p.id === price.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(p.id)}
              className={`py-1.5 rounded-md text-[11px] font-semibold transition-all truncate ${
                active ? 'bg-primary text-on-primary shadow-sm' : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {getFuelMeta(p.fuelType).tab}
            </button>
          );
        })}
      </div>

      <div className="relative bg-gradient-to-br from-primary-container to-primary text-on-primary rounded-xl p-5 shadow-lg overflow-hidden">
        <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-secondary/10 rounded-full blur-xl pointer-events-none" />
        <div className="flex items-center justify-between gap-2 text-on-primary-container text-[11px] mb-3">
          <span className="uppercase tracking-wider font-bold">{meta.previewTitle}</span>
          <span className="bg-secondary text-on-secondary px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
            Pompi Direct Deal
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 bg-tertiary-container/80 rounded-lg p-3 text-center my-3">
          <div>
            <div className="text-[11px] font-semibold text-surface-container-highest">Avg Pump Price</div>
            <div className="text-base font-semibold text-surface-container-highest line-through mt-0.5 tabular">
              {formatEuro(values.stationPrice)}/{unit}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-secondary-fixed">Pompi App Price</div>
            <div className="text-base font-bold text-secondary-fixed mt-0.5 tabular">
              {formatEuro(values.pompiPrice)}/{unit}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-3 text-center">
          <div className="text-[11px] font-semibold text-on-primary-container">Estimated saving per fill-up:</div>
          <div className="text-[32px] leading-10 font-black tracking-tight text-secondary-container mt-1 tabular">
            Save {formatEuro(perFillUp)}
          </div>
          <div className="text-xs text-on-primary-container/80 mt-1">
            Based on standard {values.sizePerFillUp} {unit} {unit === 'kWh' ? 'charge capacity' : 'tank volume'}
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-surface-container-low flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary-fixed/50 flex items-center justify-center text-secondary">
            <Icon name="calendar_month" size={20} />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-on-surface-variant">Annualized Savings (2 fills/mo)</div>
            <div className="text-base font-bold text-on-surface tabular">
              {formatEuro(perFillUp * FILLS_PER_YEAR)} / year
            </div>
          </div>
        </div>
        <Icon name="check_circle" size={22} className="text-secondary" />
      </div>
    </div>
  );
}
